import React from "react";
import { Field } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";

// Role-specific profile field schemas.
// Each entry: { key, label, type, placeholder, options?, helper?, cols? }
const SCHEMAS = {
  flight_attendant: [
    { key: "years_experience", label: "Years of Experience", type: "number", placeholder: "5", cols: 2 },
    { key: "base_airport", label: "Home Base", type: "airport", placeholder: "Type city or ICAO (KTEB, Miami…)", cols: 2 },
    { key: "languages_spoken", label: "Languages Spoken", type: "csv", placeholder: "English, Spanish, French", helper: "Comma-separated." },
    { key: "cabin_types_served", label: "Aircraft Experience", type: "csv", placeholder: "G550, Global 6000, Falcon 7X", helper: "Comma-separated types you have worked on." },
    // Annual FA training — FACTS, Cabin CSR, Emergency
    { key: "facts_last", label: "FACTS Training · Last Date", type: "date", cols: 2 },
    { key: "facts_expires", label: "FACTS Training · Expires", type: "date", cols: 2 },
    { key: "cabin_csr_last", label: "Cabin CSR · Last Date", type: "date", cols: 2 },
    { key: "cabin_csr_expires", label: "Cabin CSR · Expires", type: "date", cols: 2 },
    { key: "emergency_training_last", label: "Emergency Training · Last Date", type: "date", cols: 2 },
    { key: "emergency_training_expires", label: "Emergency Training · Expires", type: "date", cols: 2 },
    // G550 emergency exit (oval window) — signed off?
    { key: "g550_emer_exit_signed_off", label: "G550 Oval-Window Emergency Exit Trained?", type: "select", options: ["No", "Yes — signed off"], cols: 2 },
    { key: "g550_emer_exit_cert_url", label: "G550 Emer Exit Cert (link)", type: "text", placeholder: "https:// or file URL — upload via profile assets", cols: 2 },
    // Menu / cabin galleries
    { key: "menu_specialty", label: "Menu Specialty", type: "csv", placeholder: "Kosher, Halal, Vegan, Michelin-style", helper: "Comma-separated." },
    { key: "medical_first_aid", label: "Medical / First Aid", type: "select", options: ["None", "CPR / First Aid", "AED-Certified", "EMT / Paramedic"], cols: 2 },
    { key: "willing_to_travel", label: "Willing to Travel", type: "select", options: ["Domestic only", "North America", "Worldwide"], cols: 2 },
    { key: "certifications", label: "Other Certifications", type: "csv", placeholder: "Food Safety Manager, TSA Known Crewmember", helper: "Comma-separated." },
  ],
  amt: [
    { key: "ap_license_number", label: "A&P License #", type: "text", placeholder: "3712345", cols: 2 },
    { key: "ia_certified", label: "IA (Inspection Authorization)", type: "select", options: ["No", "Yes"], cols: 2 },
    { key: "specialties", label: "Specialties", type: "csv", placeholder: "Turbine, Avionics, Structures", helper: "Comma-separated." },
    { key: "aircraft_families", label: "Aircraft Families", type: "csv", placeholder: "Gulfstream, Bombardier Global, Citation", helper: "OEM lines you've worked on." },
    { key: "mro_affiliations", label: "MRO Affiliations", type: "csv", placeholder: "Duncan Aviation, StandardAero, West Star" },
    { key: "aog_available", label: "AOG Available", type: "select", options: ["No", "Regional (drivable)", "Worldwide (will fly out)"], cols: 2 },
    { key: "years_experience", label: "Years of Experience", type: "number", placeholder: "12", cols: 2 },
  ],
  fbo: [
    { key: "company_name", label: "FBO / Company Name", type: "text", placeholder: "Signature Flight Support" },
    { key: "airport_icao", label: "Primary Airport", type: "airport", placeholder: "Type city or ICAO (KTEB, Miami…)", cols: 2 },
    { key: "position_title", label: "Your Position", type: "text", placeholder: "General Manager", cols: 2 },
    { key: "services_offered", label: "Services Offered", type: "csv", placeholder: "Fuel, Hangar, Catering, Ground Transport, Customs", helper: "Comma-separated." },
    { key: "hours_of_operation", label: "Hours of Operation", type: "select", options: ["24/7", "Extended (6a-11p)", "Standard (business hours)"], cols: 2 },
    { key: "fuel_brands", label: "Fuel Brands", type: "csv", placeholder: "Jet A · Phillips 66, Avgas 100LL", cols: 2 },
    { key: "hangar_capacity", label: "Hangar Capacity", type: "text", placeholder: "Up to G650 · 30,000 sq ft" },
  ],
  charter_operator: [
    { key: "company_name", label: "Operator Name", type: "text", placeholder: "Skybound Aviation LLC" },
    { key: "part_135_certificate", label: "Part 135 Certificate #", type: "text", placeholder: "SKYA123B", cols: 2 },
    { key: "faa_operator_id", label: "FAA Operator Designator", type: "text", placeholder: "SBA", cols: 2 },
    { key: "fleet_size", label: "Fleet Size", type: "number", placeholder: "12", cols: 2 },
    { key: "primary_bases", label: "Primary Bases (ICAO)", type: "csv", placeholder: "KTEB, KVNY, KOPF", cols: 2 },
    { key: "aircraft_types_operated", label: "Aircraft Operated", type: "csv", placeholder: "G550, Global 6000, Challenger 350", helper: "Comma-separated." },
    { key: "service_regions", label: "Service Regions", type: "csv", placeholder: "North America, Caribbean, Europe", helper: "Manage from Operator Onboarding for the full multi-select." },
    { key: "hiring_status", label: "Hiring / Availability", type: "select", options: ["Available for charter requests", "Not available"], cols: 2 },
    { key: "argus_wyvern_rating", label: "Safety Rating", type: "select", options: ["None", "PJP", "ARGUS Gold", "ARGUS Gold Plus", "ARGUS Platinum", "WYVERN Wingman", "IS-BAO"], cols: 2 },
  ],
  aircraft_owner: [
    { key: "aircraft_owned", label: "Aircraft Owned", type: "csv", placeholder: "N123AB · Gulfstream G450", helper: "Tail # · Make/Model — one per line ok." },
    { key: "based_at_icao", label: "Based At", type: "airport", placeholder: "Type city or ICAO (KTEB, Miami…)", cols: 2 },
    { key: "management_company", label: "Management Company", type: "text", placeholder: "Executive Jet Management", cols: 2 },
    { key: "availability_for_charter", label: "Available For Charter?", type: "select", options: ["Not available", "Part 91 dry lease", "Part 135 charter", "Both"], cols: 2 },
    { key: "annual_hours_flown", label: "Annual Hours Flown", type: "number", placeholder: "250", cols: 2 },
    { key: "looking_for", label: "Currently Looking For", type: "csv", placeholder: "Type-rated crew, Fractional buy-in, Upgrade" },
  ],
  enthusiast: [
    { key: "company_or_family_office", label: "Company / Family Office (optional)", type: "text", placeholder: "Coursey Capital", cols: 2 },
    { key: "role_title", label: "Your Title", type: "text", placeholder: "Principal · CEO · Advisor", cols: 2 },
    { key: "based_at_icao", label: "Home Base", type: "airport", placeholder: "Type city or ICAO (KTEB, Miami…)", cols: 2 },
    { key: "preferred_aircraft_class", label: "Preferred Cabin Class", type: "select", options: ["Light", "Midsize", "Super-mid", "Large-cabin", "Ultra-long-range", "Any"], cols: 2 },
    { key: "primary_use_case", label: "Primary Use Case", type: "select", options: ["Personal travel", "Business travel", "Fractional/Card evaluation", "Ownership research"], cols: 2 },
    { key: "annual_hours_needed", label: "Annual Hours (est.)", type: "number", placeholder: "50", cols: 2 },
    { key: "interests", label: "Interests / Looking For", type: "csv", placeholder: "Charter, Empty legs, Ownership, Networking", helper: "Comma-separated." },
  ],
  aircraft_sales: [
    { key: "company_name", label: "Brokerage / Firm", type: "text", placeholder: "Guardian Jet" },
    { key: "role_title", label: "Your Title", type: "text", placeholder: "Senior Broker · AV/EXEC", cols: 2 },
    { key: "years_experience", label: "Years in Sales", type: "number", placeholder: "15", cols: 2 },
    { key: "transactions_closed", label: "Transactions Closed", type: "number", placeholder: "180", cols: 2 },
    { key: "specialties", label: "Specialties", type: "csv", placeholder: "Large-cabin, Mid-size, Fractional", cols: 2 },
    { key: "aircraft_focus", label: "Aircraft Focus", type: "csv", placeholder: "Gulfstream, Bombardier Global, Falcon", helper: "OEM lines you focus on." },
    { key: "markets_served", label: "Markets Served", type: "csv", placeholder: "North America, Europe, Middle East" },
    { key: "current_listings_count", label: "Active Listings", type: "number", placeholder: "12", cols: 2 },
    { key: "buy_requests_open", label: "Open Buy Requests?", type: "select", options: ["No", "Yes — public", "Yes — private only"], cols: 2 },
  ],
};

export function roleHasProfile(role) {
  return !!SCHEMAS[role];
}

export function roleLabel(role) {
  return ({
    pilot: "Pilot",
    flight_attendant: "Flight Attendant",
    amt: "Mechanic (A&P)",
    mechanic: "Mechanic (A&P)",
    fbo: "FBO Manager",
    charter_operator: "Charter Operator",
    operator: "Charter Operator",
    aircraft_owner: "AV/EXEC",
    aircraft_sales: "AV/EXEC · Broker",
    broker: "AV/EXEC · Broker",
    enthusiast: "AV/EXEC",
    admin: "PJP Admin",
  })[role] || role;
}

export default function RoleProfileForm({ role, values, onChange }) {
  const schema = SCHEMAS[role];
  if (!schema) return null;

  const set = (key, v) => onChange({ ...values, [key]: v });

  return (
    <div className="space-y-4">
      <div className="overline text-cyan">// {roleLabel(role).toUpperCase()} · ROLE-SPECIFIC</div>

      {schema.reduce((rows, field, idx) => {
        const last = rows[rows.length - 1];
        if (field.cols === 2 && last && last.length === 1 && last[0].cols === 2) {
          last.push(field);
        } else {
          rows.push([field]);
        }
        return rows;
      }, []).map((row, rIdx) => (
        <div key={rIdx} className={row.length === 2 ? "grid grid-cols-2 gap-3" : ""}>
          {row.map((f) => <FieldRenderer key={f.key} f={f} value={values[f.key]} onChange={(v) => set(f.key, v)} />)}
        </div>
      ))}
    </div>
  );
}

function FieldRenderer({ f, value, onChange }) {
  const testId = `role-${f.key.replaceAll("_", "-")}`;

  if (f.type === "airport") {
    return (
      <Field label={f.label}>
        <AirportInput
          value={value ?? ""}
          onChange={onChange}
          testId={testId}
          placeholder={f.placeholder}
        />
        {f.helper && <div className="mt-1 text-[11px] text-white/45">{f.helper}</div>}
      </Field>
    );
  }

  if (f.type === "select") {
    return (
      <Field label={f.label}>
        <select data-testid={testId} className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {f.helper && <div className="mt-1 text-[11px] text-white/45">{f.helper}</div>}
      </Field>
    );
  }
  if (f.type === "csv") {
    const asStr = Array.isArray(value) ? value.join(", ") : (value ?? "");
    return (
      <Field label={f.label}>
        <input
          data-testid={testId}
          className="input"
          value={asStr}
          onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder={f.placeholder}
        />
        {f.helper && <div className="mt-1 text-[11px] text-white/45">{f.helper}</div>}
      </Field>
    );
  }
  return (
    <Field label={f.label}>
      <input
        data-testid={testId}
        type={f.type}
        className="input"
        value={value ?? ""}
        onChange={(e) => onChange(f.type === "number" ? (e.target.value ? Number(e.target.value) : "") : e.target.value)}
        placeholder={f.placeholder}
      />
      {f.helper && <div className="mt-1 text-[11px] text-white/45">{f.helper}</div>}
    </Field>
  );
}
